import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import TranslateIcon from '@mui/icons-material/Translate';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import ArticleIcon from '@mui/icons-material/Article';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import logo from '../Images/Almaryah-Legal-Consultancy.png';

const Navbarsection = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const { currentLang, changeLanguage, isRTL } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  
  const isMobile = useMediaQuery('(max-width:1000px)');
  const direction = currentLang === 'ar' ? 'rtl' : 'ltr';

  // Effect for scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation items based on language
  const navItems = {
    en: [
      { text: 'Home', icon: HomeIcon, path: '/' },
      { text: 'About Us', icon: InfoIcon, path: '/about' },
      { text: 'Our Services', icon: BuildIcon, path: '/services' },
      { text: 'Blogs', icon: ArticleIcon, path: '/blogs' },
      { text: 'Contact Us', icon: ContactMailIcon, path: '/#contact' }
    ],
    ar: [
      { text: 'الرئيسية', icon: HomeIcon, path: '/' },
      { text: 'من نحن', icon: InfoIcon, path: '/about' },
      { text: 'خدماتنا', icon: BuildIcon, path: '/services' },
      { text: 'المدونة', icon: ArticleIcon, path: '/blogs' },
      { text: 'اتصل بنا', icon: ContactMailIcon, path: '/#contact' }
    ]
  };

  const currentNavItems = navItems[currentLang];

  const handleLanguageToggle = () => {
    changeLanguage(currentLang === 'en' ? 'ar' : 'en');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // دالة للتنقل مع التعامل مع روابط الهاش
  const handleNavigation = (path) => {
    setMobileOpen(false);
    
    if (path === '/#contact') {
      if (window.location.pathname === '/') {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      navigate(path);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: -50
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        type: "spring",
        stiffness: 100,
        damping: 15,
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const logoVariants = {
    hidden: { 
      opacity: 0, 
      x: direction === 'rtl' ? -50 : 50,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.8
      }
    }
  };

  const navItemVariants = {
    hidden: { 
      opacity: 0, 
      y: -30,
      scale: 0.9
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.15,
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }),
    hover: {
      scale: 1.05,
      y: -2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: { scale: 0.98 }
  };

  const buttonVariants = {
    hidden: { 
      scale: 0, 
      opacity: 0,
      rotate: -180
    },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }),
    hover: {
      scale: 1.1,
      y: -2,
      boxShadow: "0 10px 20px rgba(212,175,55,0.3)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: { scale: 0.95 }
  };

  const drawerItemVariants = {
    hidden: { 
      opacity: 0,
      x: direction === 'rtl' ? 50 : -50
    },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }),
    hover: {
      x: direction === 'rtl' ? -5 : 5,
      backgroundColor: 'rgba(212,175,55,0.15)',
      transition: {
        type: "spring",
        stiffness: 300
      }
    }
  };

  const floatingAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Mobile drawer content - الأيقونة أولاً في كل اللغات
  const drawer = (
    <Box sx={{ 
      textAlign: direction === 'rtl' ? 'right' : 'left', 
      pt: 2,
      background: '#000000',
      height: '100%',
      color: 'white',
      direction: direction
    }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 2, 
          mb: 3,
          flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
        }}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            animate={floatingAnimation}
          >
            {/* <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                fontWeight: 'bold',
                color: '#d4af37',
              }}
            >
              {currentLang === 'ar' ? 'الماريه للإستشارات القانونيه' : 'almaryah Legal Consultancy'}
            </Typography> */}
          </motion.div>
          <motion.div
            whileHover={{ rotate: 90, scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IconButton onClick={handleDrawerToggle} sx={{ color: '#d4af37' }}>
              <CloseIcon />
            </IconButton>
          </motion.div>
        </Box>
      </motion.div>

      <List>
        {currentNavItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.text}
              custom={index}
              variants={drawerItemVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
            >
              <ListItem 
                button 
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: '10px',
                  mx: 1,
                  mb: 0.5,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                  justifyContent: 'flex-start',
                  padding: direction === 'rtl' ? '8px 16px 8px 8px' : '8px 8px 8px 16px'
                }}
              >
                {/* الأيقونة - أولاً في كل اللغات (order: 1) */}
                <motion.div
                  whileHover={{ 
                    rotate: 10, 
                    scale: 1.1,
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                  style={{
                    order: 1  // الأيقونة أولاً في كل اللغات
                  }}
                >
                  <Icon sx={{ 
                    color: '#d4af37',
                    fontSize: '1.5rem'
                  }} />
                </motion.div>
                
                {/* الكلمة - ثانياً (order: 2) */}
                <ListItemText 
                  primary={item.text} 
                  sx={{ 
                    flex: '0 1 auto',
                    order: 2,  // الكلمة ثانياً في كل اللغات
                    textAlign: direction === 'rtl' ? 'right' : 'left',
                    '& .MuiListItemText-primary': { 
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                    }
                  }} 
                />
              </ListItem>
            </motion.div>
          );
        })}
      </List>

      {/* Language toggle in drawer */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 20, 
        left: 0, 
        right: 0, 
        px: 2,
        textAlign: direction === 'rtl' ? 'right' : 'left'
      }}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleLanguageToggle}
            sx={{
              color: '#d4af37',
              borderColor: '#d4af37',
              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start',
              '&:hover': {
                borderColor: '#c4a030',
                backgroundColor: 'rgba(212,175,55,0.1)'
              }
            }}
          >
            <TranslateIcon sx={{ order: 1 }} />
            <span style={{ order: 2 }}>{currentLang === 'ar' ? 'English' : 'العربية'}</span>
          </Button>
        </motion.div>
      </Box>
    </Box>
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentLang}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ width: '100%' }}
      >
        <AppBar 
          position="fixed" 
          elevation={scrolled ? 8 : 0}
          sx={{
            background: scrolled 
              ? 'rgba(0, 0, 0, 0.95)'
              : '#000000',
            backdropFilter: scrolled ? 'blur(10px)' : 'none',
            borderBottom: scrolled ? 'none' : '1px solid rgba(212,175,55,0.3)',
            transition: 'all 0.4s ease-in-out',
            boxShadow: scrolled ? '0 4px 30px rgba(212,175,55,0.2)' : 'none',
            zIndex: 30000,
          }}
        >
          <Container maxWidth="xl">
            <Toolbar 
              disableGutters 
              sx={{ 
                justifyContent: 'space-between', 
                py: 1,
                flexDirection: 'row',
              }}
            >
              {/* Logo with animation */}
              <motion.div
                variants={logoVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Almaryah Legal Consultancy"
                    sx={{ 
                      width: { xs: 80, md: 90 },
                      height: 'auto',
                      // filter: 'brightness(1.2) contrast(1.1)',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </motion.div>
                <motion.div
                  animate={floatingAnimation}
                >
                  <Typography
                    variant={isMobile ? "body1" : "h5"}
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      fontWeight: 'bold',
                      color: '#d4af37',
                      cursor: 'pointer',
                      fontSize: isMobile ? '0.9rem' : { xs: '1.2rem', md: '1.3rem' },
                      whiteSpace: 'nowrap',
                      // textShadow: '0 0 10px rgba(212,175,55,0.3)'
                    }}
                  >
                    {currentLang === 'ar' ? 'الماريه للإستشارات القانونيه' : 'Almaryah Legal Consultancy'}
                  </Typography>
                </motion.div>
              </motion.div>

              {/* Desktop Menu - الأيقونة أولاً في كل اللغات */}
              {!isMobile && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0.5,
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap'
                }}>
                  {currentNavItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.div
                        key={item.text}
                        custom={index}
                        variants={navItemVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onHoverStart={() => setHoveredButton(item.text)}
                        onHoverEnd={() => setHoveredButton(null)}
                      >
                        <Button
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            color: hoveredButton === item.text ? '#d4af37' : 'white',
                            fontSize: { xs: '0.85rem', lg: '1rem' },
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                            px: { xs: 1.5, lg: 2.5 },
                            py: 1,
                            borderRadius: '50px',
                            position: 'relative',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            minWidth: 'auto',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                            gap: 0.8,
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(212,175,55,0.1)',
                              opacity: 0,
                              transition: 'opacity 0.3s',
                              zIndex: -1,
                              borderRadius: '50px'
                            },
                            '&:hover::before': {
                              opacity: 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              width: '0%',
                              height: '2px',
                              bottom: 0,
                              insetInlineStart: '50%',
                              transform: 'translateX(-50%)',
                              background: '#d4af37',
                              transition: 'width 0.3s ease',
                              borderRadius: '2px'
                            },
                            '&:hover::after': {
                              width: '80%'
                            }
                          }}
                        >
                          {/* الأيقونة - أولاً في كل اللغات (order: 1) */}
                          <motion.div
                            animate={{ 
                              rotate: hoveredButton === item.text ? [0, 10, -10, 0] : 0,
                              scale: hoveredButton === item.text ? [1, 1.2, 1] : 1
                            }}
                            transition={{ duration: 0.3 }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              order: 1  // الأيقونة أولاً في كل اللغات
                            }}
                          >
                            <Icon sx={{ 
                              color: hoveredButton === item.text ? '#d4af37' : 'white',
                              fontSize: '1.2rem'
                            }} />
                          </motion.div>
                          
                          {/* الكلمة - ثانياً (order: 2) */}
                          <motion.span
                            animate={{ 
                              y: hoveredButton === item.text ? [-1, 1, -1] : 0
                            }}
                            transition={{ duration: 0.5, repeat: hoveredButton === item.text ? Infinity : 0 }}
                            style={{
                              order: 2  // الكلمة ثانياً في كل اللغات
                            }}
                          >
                            {item.text}
                          </motion.span>
                        </Button>
                      </motion.div>
                    );
                  })}
                </Box>
              )}

              {/* Desktop Language Toggle */}
              {!isMobile && (
                <motion.div
                  variants={buttonVariants}
                  custom={currentNavItems.length}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <IconButton
                    onClick={handleLanguageToggle}
                    sx={{
                      color: '#d4af37',
                      background: 'rgba(212,175,55,0.1)',
                      width: { xs: 40, md: 50 },
                      height: { xs: 40, md: 50 },
                      flexShrink: 0,
                      border: '1px solid rgba(212,175,55,0.3)',
                      '&:hover': {
                        background: 'rgba(212,175,55,0.2)',
                      }
                    }}
                  >
                    <motion.div
                      animate={{ rotate: hoveredButton === 'language' ? 360 : 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <TranslateIcon />
                    </motion.div>
                  </IconButton>
                </motion.div>
              )}

              {/* Mobile Menu Icon */}
              {isMobile && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    onClick={handleDrawerToggle}
                    sx={{
                      color: '#d4af37',
                      background: 'rgba(212,175,55,0.1)',
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      border: '1px solid rgba(212,175,55,0.3)',
                      '&:hover': {
                        background: 'rgba(212,175,55,0.2)',
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </motion.div>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor={direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { 
              width: 300,
              backgroundColor: '#000000',
              boxShadow: '0 0 30px rgba(212,175,55,0.2)',
              borderLeft: direction === 'rtl' ? '1px solid rgba(212,175,55,0.3)' : 'none',
              borderRight: direction === 'rtl' ? 'none' : '1px solid rgba(212,175,55,0.3)'
            }
          }}
        >
          <Box dir={direction} sx={{ height: '100%' }}>
            {drawer}
          </Box>
        </Drawer>

        {/* Animated background elements */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'fixed',
            top: '10%',
            right: '5%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
            zIndex: -1,
            pointerEvents: 'none'
          }}
        />

        <motion.div
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'fixed',
            bottom: '10%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
            zIndex: -1,
            pointerEvents: 'none'
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default Navbarsection;